import {
  Ban,
  Check,
  CheckCheck,
  Clock,
  Download,
  File as FileIcon,
  Image as ImageIcon,
  Mic,
  RotateCw,
  Smile,
  Video,
} from 'lucide-react'
import { useState, type ReactNode } from 'react'

import { mediaLabelOf } from '@aeris/status-meta'
import { Dialog, DialogContent, cn } from '@aeris/ui'

import type { MessageSummaryType } from '../lib/api.ts'
import { gatewayErrorMessage } from '../lib/gateway-errors.ts'
import { clockOf, formatWaId } from '../lib/inbox.ts'

import { AudioPlayer } from './inbox-audio.tsx'
import { MessageText } from './span-text.tsx'

/**
 * Uma mensagem na thread — F085.
 *
 * ## O que mudou em relação à F083
 *
 * A bolha existia e estava certa no essencial (canto reto do lado de quem
 * fala, hora e status dentro). O que mudou é a ESCALA e a densidade:
 *
 * - largura máxima em porcentagem, não em `rem`. Com `max-w-[min(32rem,80%)]`
 *   a bolha travava em 512px, e numa conversa de 950px isso deixava um "Oi"
 *   com a mesma largura de um parágrafo. Agora ela cresce com a coluna e para
 *   em 65%, que é onde uma linha de texto ainda é confortável de ler;
 * - o status virou ÍCONE. Um crachá "Enviado" ao lado de cada mensagem
 *   enviada é a mesma palavra repetida quarenta vezes numa tela — o duplo
 *   tique diz a mesma coisa em 12px. `failed` continua com texto, porque
 *   ali a informação é o MOTIVO, e um ícone vermelho não explica nada.
 *
 * ## A mídia (F194)
 *
 * Foto e figurinha aparecem na bolha; áudio e vídeo tocam ali; documento vira
 * um cartão com o nome e o botão de baixar. Tudo pela URL que o servidor
 * monta (`message.media.url`) — um proxy autenticado, nunca o arquivo do
 * WhatsApp direto.
 *
 * `pending` usa a mesma URL: a API busca o arquivo na primeira vez.
 * `unavailable` diz a verdade — o WhatsApp já apagou. Sem `media` (a API
 * oficial, cuja mídia ainda não é baixada), fica o cartão antigo com o tipo e
 * o aviso de abrir no WhatsApp.
 */

const STATUS_ICON: Record<MessageSummaryType['status'], ReactNode> = {
  queued: <Clock aria-hidden="true" className="h-3.5 w-3.5" />,
  sent: <Check aria-hidden="true" className="h-3.5 w-3.5" />,
  delivered: <CheckCheck aria-hidden="true" className="h-3.5 w-3.5" />,
  read: <CheckCheck aria-hidden="true" className="h-3.5 w-3.5" />,
  failed: <Ban aria-hidden="true" className="h-3.5 w-3.5" />,
}

const STATUS_LABEL: Record<MessageSummaryType['status'], string> = {
  queued: 'Enviando',
  sent: 'Enviado',
  delivered: 'Entregue',
  read: 'Lida',
  failed: 'Falhou',
}

/**
 * O tique, com a palavra atrás dele.
 *
 * O ícone é `aria-hidden` e o rótulo vai num `<span class="sr-only">`: quem
 * usa leitor de tela ouve "Entregue", não "imagem". Cor sozinha nunca carrega
 * a informação — `docs/UI_UX.md` —, e é por isso que a palavra existe mesmo
 * quando ninguém a vê.
 */
const StatusTick = ({
  status,
}: {
  status: MessageSummaryType['status']
}): ReactNode => (
  <span
    className={cn(
      'flex shrink-0 items-center',
      status === 'read' && 'text-(--info)',
      status === 'failed' && 'text-(--danger)'
    )}
  >
    {STATUS_ICON[status]}
    <span className="sr-only">{STATUS_LABEL[status]}</span>
  </span>
)

const MEDIA_ICON: Partial<Record<MessageSummaryType['kind'], ReactNode>> = {
  image: <ImageIcon aria-hidden="true" className="h-4 w-4" />,
  video: <Video aria-hidden="true" className="h-4 w-4" />,
  audio: <Mic aria-hidden="true" className="h-4 w-4" />,
  document: <FileIcon aria-hidden="true" className="h-4 w-4" />,
  sticker: <Smile aria-hidden="true" className="h-4 w-4" />,
}

// O nome da mídia mudou para `@aeris/status-meta` na F308.
export { mediaLabelOf }

/** O cartão sem arquivo: o tipo, e uma frase sobre o que dá para fazer. */
const MediaCard = ({
  message,
  note,
  action,
}: {
  message: MessageSummaryType
  note: string
  action?: ReactNode
}): ReactNode => (
  <span className="flex items-center gap-2 rounded-(--radius-md) bg-(--surface-sunken) px-2.5 py-2 text-(--text-secondary)">
    <span className="text-(--text-muted)">
      {MEDIA_ICON[message.kind] ?? (
        <FileIcon aria-hidden="true" className="h-4 w-4" />
      )}
    </span>
    <span className="flex min-w-0 flex-1 flex-col">
      <span className="truncate text-(length:--text-sm) font-(--weight-medium) text-(--text-primary)">
        {message.media?.fileName ?? mediaLabelOf(message.kind)}
      </span>
      <span className="text-(length:--text-xs) text-(--text-muted)">
        {note}
      </span>
    </span>
    {action}
  </span>
)

/** `1,2 MB` — o tamanho como alguém fala dele. */
export const formatBytes = (bytes: number | null): string | null => {
  if (bytes === null) return null
  if (bytes < 1024) return `${String(bytes)} B`
  const units = ['KB', 'MB', 'GB']
  let value = bytes / 1024
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit += 1
  }
  return `${value.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} ${units[unit] ?? 'GB'}`
}

/** `0:07` — a duração de um áudio. */
export const formatSeconds = (seconds: number | null): string | null =>
  seconds === null
    ? null
    : `${String(Math.floor(seconds / 60))}:${String(seconds % 60).padStart(2, '0')}`

const MessageMedia = ({
  message,
}: {
  message: MessageSummaryType
}): ReactNode => {
  // Trocar a chave recria o elemento e pede o arquivo de novo.
  const [attempt, setAttempt] = useState(0)
  const [failed, setFailed] = useState(false)
  /** A foto grande, numa janela por cima da conversa (03/10). */
  const [viewing, setViewing] = useState(false)
  const media = message.media

  if (media === null) {
    return (
      <MediaCard
        message={message}
        note="Ainda não é exibido aqui — abra no WhatsApp"
      />
    )
  }
  if (media.status === 'unavailable') {
    return (
      <MediaCard
        message={message}
        note="O WhatsApp não guarda mais este arquivo"
      />
    )
  }
  if (failed) {
    return (
      <MediaCard
        message={message}
        note="Não foi possível carregar agora"
        action={
          <button
            type="button"
            className="flex items-center gap-1 rounded-(--radius-sm) px-1.5 py-1 text-(length:--text-xs) text-(--text-secondary) hover:bg-(--surface-hover)"
            onClick={() => {
              setFailed(false)
              setAttempt((value) => value + 1)
            }}
          >
            <RotateCw aria-hidden="true" className="h-3.5 w-3.5" />
            Tentar de novo
          </button>
        }
      />
    )
  }

  const src =
    attempt === 0 ? media.url : `${media.url}?tentativa=${String(attempt)}`
  const onError = (): void => {
    setFailed(true)
  }

  if (message.kind === 'image' || message.kind === 'sticker') {
    /*
     * Numa janela por cima da conversa, e não noutra aba (03/10): a loja
     * clicava, perdia a conversa de vista e tinha de achar a aba de volta.
     */
    return (
      <>
        <button
          type="button"
          onClick={() => {
            setViewing(true)
          }}
          className="block cursor-zoom-in rounded-(--radius-md) focus-visible:outline-2 focus-visible:outline-(--border-focus)"
          aria-label={`Abrir ${mediaLabelOf(message.kind).toLowerCase()} em tamanho real`}
        >
          <img
            key={attempt}
            src={src}
            alt={message.body ?? mediaLabelOf(message.kind)}
            loading="lazy"
            onError={onError}
            className={cn(
              'rounded-(--radius-md) object-contain',
              message.kind === 'sticker'
                ? 'h-32 w-32'
                : 'max-h-80 w-full max-w-72'
            )}
          />
        </button>
        <Dialog open={viewing} onOpenChange={setViewing}>
          <DialogContent
            title={mediaLabelOf(message.kind)}
            {...(message.body === null ? {} : { description: message.body })}
            className="max-w-[min(64rem,calc(100vw-2rem))]"
          >
            <div className="flex flex-col items-center gap-3">
              <img
                src={src}
                alt={message.body ?? mediaLabelOf(message.kind)}
                className="max-h-[75dvh] w-auto max-w-full rounded-(--radius-md) object-contain"
              />
              <a
                href={media.url}
                download={media.fileName ?? true}
                className="flex items-center gap-1 rounded-(--radius-sm) px-2 py-1 text-(length:--text-sm) text-(--text-secondary) hover:bg-(--surface-hover)"
              >
                <Download aria-hidden="true" className="h-4 w-4" />
                Baixar
              </a>
            </div>
          </DialogContent>
        </Dialog>
      </>
    )
  }

  if (message.kind === 'audio') {
    return (
      <span className="flex flex-col gap-0.5">
        <AudioPlayer
          key={attempt}
          src={src}
          seconds={media.seconds}
          onError={onError}
          outgoing={message.direction === 'out'}
        />
        {media.seconds === null ? null : (
          <span className="sr-only">
            Áudio de {formatSeconds(media.seconds)}
          </span>
        )}
      </span>
    )
  }

  if (message.kind === 'video') {
    return (
      <video
        key={attempt}
        controls
        preload="metadata"
        src={src}
        onError={onError}
        className="max-h-80 w-full max-w-72 rounded-(--radius-md)"
      >
        <track kind="captions" />
      </video>
    )
  }

  return (
    <MediaCard
      message={message}
      note={[mediaLabelOf(message.kind), formatBytes(media.byteSize)]
        .filter((part): part is string => part !== null)
        .join(' · ')}
      action={
        <a
          href={media.url}
          download={media.fileName ?? true}
          className="flex items-center gap-1 rounded-(--radius-sm) px-1.5 py-1 text-(length:--text-xs) text-(--text-secondary) hover:bg-(--surface-hover)"
        >
          <Download aria-hidden="true" className="h-3.5 w-3.5" />
          Baixar
        </a>
      }
    />
  )
}

export const MessageBubble = ({
  message,
}: {
  message: MessageSummaryType
}): ReactNode => {
  const outgoing = message.direction === 'out'
  const hasMedia = message.kind !== 'text' && message.kind !== 'template'
  const author =
    message.participantName ??
    (message.participantPhone === null
      ? null
      : formatWaId(message.participantPhone))

  return (
    <li
      className={cn(
        'flex max-w-[85%] flex-col sm:max-w-[65%]',
        outgoing ? 'ml-auto items-end' : 'items-start'
      )}
    >
      <div
        className={cn(
          'flex flex-col gap-1 px-2.5 py-1.5 text-(length:--text-sm)',
          'whitespace-pre-wrap break-words shadow-(--shadow-sm)',
          outgoing
            ? // O canto "reto" do lado de quem fala dá a direção da mensagem
              // sem precisar de seta.
              'rounded-(--radius-lg) rounded-br-(--radius-sm) bg-(--brand-primary-subtle) text-(--text-primary)'
            : 'rounded-(--radius-lg) rounded-bl-(--radius-sm) bg-(--surface-raised) text-(--text-primary)'
        )}
      >
        {/*
         * Num grupo (F268), quem escreveu: são vários funcionários da mesma
         * loja, e a resposta depende de quem perguntou.
         */}
        {!outgoing && author !== null ? (
          <span className="text-(length:--text-xs) font-(--weight-semibold) text-(--brand-primary)">
            {author}
          </span>
        ) : null}

        {hasMedia ? <MessageMedia message={message} /> : null}

        {message.body === null ? null : (
          /*
           * O corpo guardado está em MARKDOWN, e a tela mostrava a marcação
           * crua: o atendente lia `**Thiago:**` enquanto o cliente via negrito
           * (F083). Nunca `dangerouslySetInnerHTML` — o texto vem do cliente.
           */
          <span>
            <MessageText body={message.body} />
          </span>
        )}

        {/*
         * Hora e status DENTRO da bolha, à direita — é o que faz uma conversa
         * parecer uma conversa em vez de uma lista de itens com legenda
         * embaixo, e economiza uma linha por mensagem.
         */}
        <span className="flex items-center justify-end gap-1 self-end text-(length:--text-xs) text-(--text-muted)">
          {/* A mensagem que a venda mandou sozinha (F245/F246). */}
          {message.origin === 'automation' ? <span>automático ·</span> : null}
          {clockOf(message.occurredAt)}
          {outgoing ? <StatusTick status={message.status} /> : null}
        </span>
      </div>

      {/*
       * O motivo da falha fica FORA da bolha, e é a única coisa que fica: uma
       * mensagem que não chegou precisa dizer por quê, e essa frase não é
       * conteúdo da conversa — é um aviso sobre ela.
       */}
      {message.status === 'failed' && message.errorCode !== null ? (
        <span className="mt-0.5 max-w-full text-right text-(length:--text-xs) text-(--danger)">
          {gatewayErrorMessage(message.errorCode)}
        </span>
      ) : null}
    </li>
  )
}

/** "Hoje", "Ontem" ou a data — um chip central, nunca uma faixa de ponta a ponta. */
export const DateChip = ({ label }: { label: string }): ReactNode => (
  <li className="flex justify-center py-2">
    <span className="rounded-(--radius-full) bg-(--surface) px-2.5 py-0.5 text-(length:--text-xs) text-(--text-muted) shadow-(--shadow-sm)">
      {label}
    </span>
  </li>
)
